import {makeAutoObservable, observable} from 'mobx'
import {DataObjectId, Hub} from 'yougile-model'
import {Chat} from '@api/model/chat'
import {EventBus} from '@client/event-bus'
import {minimizeChatEvent, restoreChatEvent} from '@client/events'
import {Tracking, TrackingEvent} from '@common/tracking'
import {crmStore} from '@components/crm/crm-store'
import {createUseStore} from '@containers/hooks'
import {
  AppPersistence,
  AppState,
  ChatFilterState,
  TaskChatTab
} from '@extensions/app-persistence'
import {Task} from '@extensions/task'

type TaskChatListOpenedBlock = '' | 'search' | 'settings'

interface State {
  chatFilter: ChatFilterState
  chatPanel: ChatPanelState
  currentTabByType: {
    crm: TaskChatTab
    default: TaskChatTab
  }
  autofocus: boolean
  currentChat?: {id: DataObjectId; companyId: DataObjectId}
  taskChatList: {openedBlock: TaskChatListOpenedBlock}
  showPopupVoting: boolean
  popupVotingType: 'create' | 'edit'
  popupVotingTaskId?: DataObjectId
}

export class RightPanelStore {
  state: State = {
    chatFilter: {
      personal: true,
      myTasks: true,
      otherTasks: false,
      groupChats: true,
      starredOnTop: true,
      unreadOnTop: false,
      privateTasks: false
    },
    chatPanel: {
      opened: false,
      width: chatPanelMinWidth,
      groupChatEdit: false
    },
    currentTabByType: {
      crm: 'dealHistory',
      default: 'chat'
    },
    autofocus: true,
    taskChatList: {
      openedBlock: ''
    },
    showPopupVoting: false,
    popupVotingType: 'create'
  }

  private readonly allowedTabsByType: Record<'crm' | 'default', TaskChatTab[]> =
    {
      crm: ['dealHistory', 'aboutClient', 'chat'],
      default: ['chat', 'connected', 'description', 'checklists']
    }

  private lastTabByType: Record<'crm' | 'default', TaskChatTab> = {
    crm: 'dealHistory',
    default: 'chat'
  }

  constructor() {
    makeAutoObservable(this, {state: observable.deep}, {autoBind: true})
  }

  get currentTab(): TaskChatTab {
    const task =
      this.state.currentChat?.id && Task.getById(this.state.currentChat.id)
    const isDeal = crmStore.isDeal(task)
    const type = isDeal ? 'crm' : 'default'

    return this.state.currentTabByType[type]
  }

  init(appState: AppState | undefined): void {
    EventBus.on(minimizeChatEvent, this.minimize)
    EventBus.on(restoreChatEvent, this.restore)

    if (!appState) {
      return
    }

    this.state.chatFilter = appState.chatFilter
    this.state.chatPanel = appState.chatPanel

    const tab = appState.taskChat?.tab

    if (tab === 'chat' && appState.page?.openedChat) {
      this.state.currentChat = appState.page.openedChat
    }

    this.lastTabByType.crm = 'dealHistory'
    this.state.currentTabByType.crm = 'dealHistory'

    if (this.allowedTabsByType.default.includes(tab)) {
      this.lastTabByType.default = tab
      this.state.currentTabByType.default = tab
    }
  }

  chatPanelSetWidth(width: number): void {
    this.state.chatPanel.width = width
    AppPersistence.save()
  }

  chatPanelSetResize(val = true): void {
    this.state.chatPanel.resize = val
  }

  setGroupChatEdit(val: boolean): void {
    this.state.chatPanel.groupChatEdit = val
  }

  chatPanelToggle(): void {
    if (!this.state.chatPanel.opened) {
      void Tracking.send(TrackingEvent.chatPanelOpen)
      EventBus.dispatch(restoreChatEvent())
    } else {
      void Tracking.send(TrackingEvent.chatPanelClose)
      EventBus.dispatch(minimizeChatEvent())
    }
  }

  setCurrentChat(id: DataObjectId, companyId: DataObjectId): void {
    this.state.currentChat = {id, companyId}
  }

  resetCurrentChat(): void {
    this.state.currentChat = undefined
  }

  setTab(tab: TaskChatTab, autofocus = true): void {
    const task =
      this.state.currentChat?.id && Task.getById(this.state.currentChat.id)
    const isDeal = crmStore.isDeal(task)
    const type = isDeal ? 'crm' : 'default'
    if (tab === 'description') {
      void Tracking.send(TrackingEvent.taskDescriptionOpenDescriptionTab)
    }
    this.lastTabByType[type] = tab
    this.state.currentTabByType[type] = tab
    this.state.autofocus = autofocus
    AppPersistence.save()
  }

  setAutofocus(autofocus = true): void {
    this.state.autofocus = autofocus
  }

  chatFilterToggle(val: ChatFilterKey): void {
    this.state.chatFilter[val] = !this.state.chatFilter[val]
    AppPersistence.save()
  }

  toggleTaskChatListOpenedBlock(block: TaskChatListOpenedBlock) {
    return () => {
      this.state.taskChatList.openedBlock =
        this.state.taskChatList.openedBlock === block ? '' : block
    }
  }

  closeTaskChatListOpenedBlock(block: TaskChatListOpenedBlock) {
    return (): boolean | void => {
      if (this.state.taskChatList.openedBlock === block) {
        this.state.taskChatList.openedBlock = ''
        return true
      }
    }
  }

  showPopupVoting(type: 'create' | 'edit', taskId: DataObjectId): void {
    this.state.showPopupVoting = true
    this.state.popupVotingType = type
    this.state.popupVotingTaskId = taskId
    Chat.showPopupVoting()
  }

  closePopupVoting(): void {
    this.state.showPopupVoting = false
    this.state.popupVotingTaskId = undefined
  }

  updateTabOnProjectChange(): void {
    const task =
      this.state.currentChat?.id && Task.getById(this.state.currentChat.id)
    const isDeal = crmStore.isDeal(task)
    const type: 'crm' | 'default' = isDeal ? 'crm' : 'default'

    const allowedTabs = this.allowedTabsByType[type]
    const lastTab = this.lastTabByType[type]

    if (!allowedTabs.includes(this.state.currentTabByType[type])) {
      this.state.currentTabByType[type] = allowedTabs.includes(lastTab)
        ? lastTab
        : allowedTabs[0]
    }

    if (!this.state.currentTabByType[type] && allowedTabs.length > 0) {
      this.state.currentTabByType[type] = allowedTabs[0]
    }
  }

  syncTabByHub(hub?: Hub): void {
    if (!hub) {
      return
    }

    const isDeal = crmStore.isDeal(hub)
    const type = isDeal ? 'crm' : 'default'
    const allowedTabs = this.allowedTabsByType[type]
    const current = this.state.currentTabByType[type]

    if (!allowedTabs.includes(current)) {
      const lastSavedTab = this.lastTabByType[type]
      this.state.currentTabByType[type] = allowedTabs.includes(lastSavedTab)
        ? lastSavedTab
        : allowedTabs[0]
    }
  }

  private minimize(): void {
    rightPanelStore.setChatPanelOpen(false)
  }

  private restore(): void {
    rightPanelStore.setChatPanelOpen(true)
  }

  private setChatPanelOpen(value: boolean): void {
    this.state.chatPanel.opened = value
    if (this.state.chatPanel.groupChatEdit) {
      this.state.chatPanel.groupChatEdit = false
    }
    AppPersistence.save()
  }
}

export type ChatFilterKey = keyof ChatFilterState

export type ChatPanelState = {
  opened: boolean
  width: number
  groupChatEdit: boolean
  resize?: boolean
}

export const chatPanelMinWidth = 440

export const [RightPanelContext, useRightPanelStore] =
  createUseStore(RightPanelStore)

export const rightPanelStore = new RightPanelStore()
